import type {
  PartnerCommissionStatus,
  PartnerDashboard,
  PartnerPayoutStatus,
  PartnerPortalCommissionListItem,
  PartnerPortalMe,
  PartnerPortalPayoutListItem,
  PartnerPortalReferralListItem,
  PartnerReferralSourceType,
  PartnerReferralStatus,
  PartnerStatus,
  PartnerUserRole,
} from '@accestra/types';
import type { BadgeVariant } from '../components/ui/Badge';

export interface PartnerMeResponse {
  me: PartnerPortalMe;
}

export interface PartnerDashboardResponse {
  dashboard: PartnerDashboard;
}

export interface PartnerReferralsResponse {
  referrals: PartnerPortalReferralListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PartnerCommissionsResponse {
  commissions: PartnerPortalCommissionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PartnerPayoutsResponse {
  payouts: PartnerPortalPayoutListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export function formatPartnerMoney(currency: string, amount: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

export function referralStatusBadgeVariant(status: PartnerReferralStatus): BadgeVariant {
  switch (status) {
    case 'customer':
      return 'success';
    case 'qualified':
    case 'trial':
      return 'info';
    case 'closed_lost':
      return 'neutral';
    case 'lead':
    default:
      return 'warning';
  }
}

export function commissionStatusBadgeVariant(status: PartnerCommissionStatus): BadgeVariant {
  switch (status) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'info';
    case 'voided':
      return 'neutral';
    case 'pending':
    default:
      return 'warning';
  }
}

export function payoutStatusBadgeVariant(status: PartnerPayoutStatus): BadgeVariant {
  switch (status) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'info';
    case 'cancelled':
      return 'neutral';
    case 'draft':
    default:
      return 'warning';
  }
}

export function partnerStatusBadgeVariant(status: PartnerStatus): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function referralStatusLabel(
  status: PartnerReferralStatus,
  t: (key: string) => string,
): string {
  return t(`statuses.${status}`);
}

export function commissionStatusLabel(
  status: PartnerCommissionStatus,
  t: (key: string) => string,
): string {
  const specificKey = `commissionStatuses.${status}`;
  const specificValue = t(specificKey);
  return specificValue === specificKey ? t(`statuses.${status}`) : specificValue;
}

export function payoutStatusLabel(
  status: PartnerPayoutStatus,
  t: (key: string) => string,
): string {
  const specificKey = `payoutStatuses.${status}`;
  const specificValue = t(specificKey);
  return specificValue === specificKey ? t(`statuses.${status}`) : specificValue;
}

export function partnerStatusLabel(
  status: PartnerStatus,
  t: (key: string) => string,
): string {
  return t(`statuses.${status}`);
}

export function partnerRoleLabel(
  role: PartnerUserRole,
  t: (key: string) => string,
): string {
  return t(`statuses.${role}`);
}

export function referralSourceLabel(
  sourceType: PartnerReferralSourceType,
  t: (key: string) => string,
): string {
  return t(`sourceTypes.${sourceType}`);
}

export async function copyTextToClipboard(value: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
